'use client';

import { Content } from '@prismicio/client';
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';
import { FC, useEffect, useState } from 'react';

import { NumbersProgress, SlideList } from '@/components';
import AnimationContainer from '@/components/Animation/AnimationContainer';
import { useIncrement, useVisible } from '@/hooks';
import styles from './GetFundsInner.module.scss';

const animationPaths = [
  '/static/animations/getTheFunds/anim1.json',
  '/static/animations/getTheFunds/anim2.json',
  '/static/animations/getTheFunds/anim3.json',
];

const titleComponents: JSXMapSerializer = {
  heading2: ({ children }) => <h2 className={styles.title}>{children}</h2>,
};

const subtitleComponents: JSXMapSerializer = {
  paragraph: ({ children }) => <p className={styles.subtitle}>{children}</p>,
};

type Props = {
  slice: Content.GetFundsSlice;
};

const fetchAnimation = async (path: string) => {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to fetch animation: ${path}`);
  }
  return response.json();
};

export const GetFundsInner: FC<Props> = ({ slice }) => {
  const [isVisibleInner, innerId] = useVisible();
  const [animationList, setAnimationList] = useState<object[]>([]);

  const cardsLength = slice.primary.cards.length;
  const [activeIndex, setActiveIndex] = useIncrement(
    cardsLength,
    {},
    isVisibleInner
  );

  const isVisibleStyles = isVisibleInner ? styles.isVisible : '';

  const animationStyles = `${styles.animatedWrapper} ${isVisibleStyles}`;

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const fetchedVideos = await Promise.all(
          animationPaths.map((path) => fetchAnimation(path))
        );
        setAnimationList(fetchedVideos);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching animations:', error);
      }
    };

    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationPaths]);

  return (
    <div id={innerId} className={styles.inner}>
      <div className={styles.textContent}>
        <div className={animationStyles}>
          <SlideList
            list={slice.primary.cards}
            activeIndex={activeIndex}
            listStyles={styles.titles}
            cardStyles={styles.card}
          >
            {(card) => (
              <PrismicRichText
                field={card.title}
                components={titleComponents}
              />
            )}
          </SlideList>
        </div>
        <div className={animationStyles}>
          <SlideList
            list={slice.primary.cards}
            activeIndex={activeIndex}
            listStyles={styles.subtitles}
            cardStyles={styles.cardSubtitle}
          >
            {(card) => (
              <PrismicRichText
                field={card.subtitle}
                components={subtitleComponents}
              />
            )}
          </SlideList>
        </div>
        <NumbersProgress
          length={slice.primary.cards.length}
          isVisible={isVisibleInner}
          activeItem={activeIndex}
          onClick={setActiveIndex}
        />
      </div>
      <div className={animationStyles}>
        <SlideList
          list={animationList}
          activeIndex={activeIndex}
          cardStyles={styles.animationItem}
          listStyles={styles.animatedList}
        >
          {(animation, index) => (
            <AnimationContainer
              className={`${styles.animationContainer} ${index !== 2 ? styles.scaled : ''} ${index === 1 ? styles.phoneAnim : ''}`}
              animation={animation}
              duration={8}
              isActive={index === activeIndex}
              play={index === activeIndex && isVisibleInner}
              isList
            />
          )}
        </SlideList>
      </div>
    </div>
  );
};
